import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { AlertTriangle, RefreshCw, Shield, UserX, Users } from 'lucide-react'
import {
  ServerApiClient,
  type FounderActivityDashboardResponse,
  type FounderActivityErrorRow,
  type FounderActivityUserRow,
} from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'
import { FuseGridSurfaceShell } from '../chrome/FuseGridSurfaceShell'

type Props = {
  topInsetPx?: number
}

const panel: CSSProperties = {
  border: '1px solid #d9e2df',
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
}

const buttonStyle: CSSProperties = {
  border: '1px solid #c8d7d2',
  background: '#fff',
  color: '#15343b',
  borderRadius: 7,
  padding: '7px 10px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
}

const inputStyle: CSSProperties = {
  border: '1px solid #c8d7d2',
  borderRadius: 7,
  padding: '7px 9px',
  fontSize: 12,
  color: '#15343b',
  minWidth: 120,
}

const thStyle: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  color: '#5d6d72',
  fontWeight: 800,
  borderBottom: '1px solid #e4ece9',
  padding: '8px 10px',
  whiteSpace: 'nowrap',
}

const tdStyle: CSSProperties = {
  fontSize: 12,
  color: '#223236',
  borderBottom: '1px solid #eef3f1',
  padding: '8px 10px',
  verticalAlign: 'top',
}

const fmtInt = (value: number | null | undefined): string =>
  Math.max(0, Math.round(Number(value || 0))).toLocaleString()

const fmtDuration = (ms: number | null | undefined): string => {
  const total = Math.max(0, Math.round(Number(ms || 0) / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h) return `${h}h ${m}m`
  if (m) return `${m}m ${s}s`
  return `${s}s`
}

const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const short = (value: string | null | undefined, max = 64): string => {
  const s = String(value || '').trim()
  if (!s) return '-'
  return s.length > max ? `${s.slice(0, max - 1)}...` : s
}

const cfoPageLabels: Record<string, string> = {
  home: 'Overview',
  financials: 'Financials',
  forecast: 'Forecast Studio',
  ar: 'A/R',
  growth: 'Growth Analysis',
  'gl-detail': 'GL Detail',
  presentation: 'Reports',
  settings: 'Settings',
}

const cfoAreaLabel = (value: string | null | undefined): string => {
  const key = String(value || '').trim()
  if (!key) return '(unknown)'
  return cfoPageLabels[key] || key.replace(/[-_]+/g, ' ')
}

const contextText = (row: FounderActivityErrorRow, key: string): string => {
  const value = row.context?.[key]
  return typeof value === 'string' ? value.trim() : ''
}

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const isBlankOrUuid = (value: string): boolean => {
  const s = value.trim()
  return !s || uuidPattern.test(s)
}

function MetricCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ ...panel, padding: 12, display: 'grid', gap: 5, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#657478', fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 22, color: '#172d33', fontWeight: 850, lineHeight: 1 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: '#7b898d' }}>{sub}</div> : null}
    </div>
  )
}

function TableShell({ children }: { children: React.ReactNode }) {
  return <div style={{ ...panel, overflow: 'auto' }}>{children}</div>
}

export function FounderActivityDashboard({ topInsetPx = 0 }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  const loadSeqRef = useRef(0)
  const didInitialLoadRef = useRef(false)
  const [windowDays, setWindowDays] = useState(30)
  const [limit, setLimit] = useState(100)
  const [siteId, setSiteId] = useState('')
  const [userId, setUserId] = useState('')
  const [selectedUser, setSelectedUser] = useState<FounderActivityUserRow | null>(null)
  const [data, setData] = useState<FounderActivityDashboardResponse | null>(null)
  const [selectedError, setSelectedError] = useState<FounderActivityErrorRow | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (overrides?: { siteId?: string; userId?: string }) => {
    const effectiveSiteId = overrides?.siteId ?? siteId
    const effectiveUserId = overrides?.userId ?? userId
    if (!isBlankOrUuid(effectiveSiteId)) {
      setError('Site id must be a UUID.')
      return
    }
    if (!isBlankOrUuid(effectiveUserId)) {
      setError('User id must be a UUID.')
      return
    }
    const requestId = loadSeqRef.current + 1
    loadSeqRef.current = requestId
    setLoading(true)
    setError('')
    try {
      const session = await ensureApiSessionReady()
      if (requestId !== loadSeqRef.current) return
      if (!session.token) {
        const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
        window.history.replaceState(window.history.state, '', `/login?next=${encodeURIComponent(next)}`)
        window.dispatchEvent(new Event('fg_route_change'))
        return
      }
      const out = await api.getFounderActivityDashboard({
        window_days: windowDays,
        limit,
        site_id: effectiveSiteId.trim() || null,
        user_id: effectiveUserId.trim() || null,
      })
      if (requestId !== loadSeqRef.current) return
      setData(out)
      setSelectedError((current) => {
        if (!current) return out.errors[0] || null
        return out.errors.find((row) => row.id === current.id) || out.errors[0] || null
      })
      if (effectiveUserId.trim()) {
        setSelectedUser((current) => {
          const selected = out.users.find((row) => row.user_id === effectiveUserId.trim())
          return selected || current
        })
      } else {
        setSelectedUser(null)
      }
    } catch (err: any) {
      if (requestId !== loadSeqRef.current) return
      const status = Number(err?.status || 0)
      setError(status === 403
        ? 'This dashboard is restricted to configured founder accounts.'
        : String(err?.body || err?.message || err || 'Could not load founder activity.'))
    } finally {
      if (requestId === loadSeqRef.current) setLoading(false)
    }
  }, [api, limit, siteId, userId, windowDays])

  const applyFilters = useCallback(() => {
    const trimmedUserId = userId.trim()
    if (trimmedUserId) {
      const knownUser = data?.users.find((row) => row.user_id === trimmedUserId) || null
      setSelectedUser(knownUser)
    } else {
      setSelectedUser(null)
    }
    void load()
  }, [data?.users, load, userId])

  const selectUser = useCallback((row: FounderActivityUserRow) => {
    setUserId(row.user_id)
    setSelectedUser(row)
    void load({ userId: row.user_id })
  }, [load])

  const clearSelectedUser = useCallback(() => {
    setUserId('')
    setSelectedUser(null)
    void load({ userId: '' })
  }, [load])

  useEffect(() => {
    if (didInitialLoadRef.current) return
    didInitialLoadRef.current = true
    void load()
  }, [load])

  const summary = data?.summary

  return (
    <FuseGridSurfaceShell
      className="fg-founder-activity"
      title="Founder Activity"
      subtitle="Customer usage, sessions, pages, and errors"
      topInsetPx={topInsetPx}
      actions={(
        <>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#52656b', fontWeight: 700 }}>
            <Shield size={14} /> Founder only
          </span>
          <button type="button" style={buttonStyle} onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </>
      )}
    >
      <div style={{ padding: 18, display: 'grid', gap: 14, background: '#f6f8f6', minHeight: '100%' }}>
        <div style={{ ...panel, padding: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 11, color: '#647276', fontWeight: 800 }}>
            Window days
            <input
              style={{ ...inputStyle, width: 90, minWidth: 90 }}
              type="number"
              min={1}
              max={90}
              value={windowDays}
              onChange={(event) => setWindowDays(Number(event.target.value) || 30)}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 11, color: '#647276', fontWeight: 800 }}>
            Limit
            <input
              style={{ ...inputStyle, width: 90, minWidth: 90 }}
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value) || 100)}
            />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 11, color: '#647276', fontWeight: 800, flex: '1 1 260px' }}>
            Site id
            <input style={inputStyle} value={siteId} onChange={(event) => setSiteId(event.target.value)} placeholder="Optional UUID filter" />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 11, color: '#647276', fontWeight: 800, flex: '1 1 260px' }}>
            User id
            <input style={inputStyle} value={userId} onChange={(event) => setUserId(event.target.value)} placeholder="Optional UUID filter" />
          </label>
          <button type="button" style={{ ...buttonStyle, background: '#1b5b62', color: '#fff', borderColor: '#1b5b62', alignSelf: 'end' }} onClick={applyFilters} disabled={loading}>
            Apply
          </button>
          {selectedUser ? (
            <button type="button" style={{ ...buttonStyle, alignSelf: 'end' }} onClick={clearSelectedUser} disabled={loading}>
              <UserX size={14} /> {short(selectedUser.email, 38)}
            </button>
          ) : null}
        </div>

        {error ? (
          <div role="alert" style={{ border: '1px solid #efc2b7', background: '#fff2ee', color: '#9f321f', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
            {error}
          </div>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(120px, 1fr))', gap: 10 }}>
          <MetricCard label="Active users" value={fmtInt(summary?.active_users)} sub={`Since ${fmtDate(summary?.since)}`} />
          <MetricCard label="Sessions" value={fmtInt(summary?.sessions)} />
          <MetricCard label="Logins" value={fmtInt(summary?.logins)} />
          <MetricCard label="Page views" value={fmtInt(summary?.page_views)} />
          <MetricCard label="Time on site" value={fmtDuration(summary?.dwell_ms)} />
          <MetricCard label="Errors" value={fmtInt(summary?.errors)} />
        </div>

        <section style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#172d33', fontWeight: 850 }}>
            <Users size={16} /> Users
          </div>
          <TableShell>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead>
                <tr>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Sites</th>
                  <th style={thStyle}>Last seen</th>
                  <th style={thStyle}>Logins</th>
                  <th style={thStyle}>Sessions</th>
                  <th style={thStyle}>Views</th>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>Errors</th>
                </tr>
              </thead>
              <tbody>
                {(data?.users || []).map((row) => {
                  const isSelected = selectedUser?.user_id === row.user_id || userId.trim() === row.user_id
                  return (
                  <tr
                    key={row.user_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectUser(row)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        selectUser(row)
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      background: isSelected ? '#edf6f4' : '#fff',
                      outline: isSelected ? '2px solid #8fbcb8' : 'none',
                      outlineOffset: -2,
                    }}
                  >
                    <td style={tdStyle}><b>{row.email}</b><div style={{ color: '#7a878a' }}>{row.user_id}</div></td>
                    <td style={tdStyle}>{row.site_names.length ? row.site_names.join(', ') : '-'}</td>
                    <td style={tdStyle}>{fmtDate(row.last_seen_at)}</td>
                    <td style={tdStyle}>{fmtInt(row.login_count)}</td>
                    <td style={tdStyle}>{fmtInt(row.session_count)}</td>
                    <td style={tdStyle}>{fmtInt(row.page_view_count)}</td>
                    <td style={tdStyle}>{fmtDuration(row.dwell_ms)}</td>
                    <td style={tdStyle}>{fmtInt(row.error_count)}</td>
                  </tr>
                  )
                })}
                {!loading && !(data?.users || []).length ? (
                  <tr><td style={tdStyle} colSpan={8}>No user activity in this window.</td></tr>
                ) : null}
              </tbody>
            </table>
          </TableShell>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(340px, 0.9fr)', gap: 12 }}>
          <section style={{ display: 'grid', gap: 8, minWidth: 0 }}>
            <div style={{ color: '#172d33', fontWeight: 850 }}>CFO Core detail</div>
            <TableShell>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Area</th>
                    <th style={thStyle}>Workbook</th>
                    <th style={thStyle}>Views</th>
                    <th style={thStyle}>Sessions</th>
                    <th style={thStyle}>Time</th>
                    <th style={thStyle}>Errors</th>
                    <th style={thStyle}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.cfo_areas || []).map((row, index) => (
                    <tr key={`${row.cfo_page}:${row.workbook_id || ''}:${index}`}>
                      <td style={tdStyle}><b>{cfoAreaLabel(row.cfo_page)}</b><div style={{ color: '#7a878a' }}>{short(row.page_title, 60)}</div></td>
                      <td style={tdStyle}>{row.site_slug || short(row.workbook_id, 28)}</td>
                      <td style={tdStyle}>{fmtInt(row.page_view_count)}</td>
                      <td style={tdStyle}>{fmtInt(row.unique_sessions)}</td>
                      <td style={tdStyle}>{fmtDuration(row.dwell_ms)}</td>
                      <td style={tdStyle}>{fmtInt(row.error_count)}</td>
                      <td style={tdStyle}>{fmtDate(row.last_seen_at)}</td>
                    </tr>
                  ))}
                  {!loading && !(data?.cfo_areas || []).length ? (
                    <tr><td style={tdStyle} colSpan={7}>No CFO Core activity in this window.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </TableShell>
          </section>

          <section style={{ display: 'grid', gap: 8, minWidth: 0 }}>
            <div style={{ color: '#172d33', fontWeight: 850 }}>Pages</div>
            <TableShell>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Path</th>
                    <th style={thStyle}>Surface</th>
                    <th style={thStyle}>Views</th>
                    <th style={thStyle}>Users</th>
                    <th style={thStyle}>Time</th>
                    <th style={thStyle}>Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.pages || []).map((row, index) => (
                    <tr key={`${row.path}:${row.surface || ''}:${index}`}>
                      <td style={tdStyle}><b>{short(row.path, 80)}</b><div style={{ color: '#7a878a' }}>{row.site_slug || row.workbook_id || '-'}</div></td>
                      <td style={tdStyle}>{row.surface || '-'}</td>
                      <td style={tdStyle}>{fmtInt(row.page_view_count)}</td>
                      <td style={tdStyle}>{fmtInt(row.unique_users)}</td>
                      <td style={tdStyle}>{fmtDuration(row.dwell_ms)}</td>
                      <td style={tdStyle}>{fmtInt(row.error_count)}</td>
                    </tr>
                  ))}
                  {!loading && !(data?.pages || []).length ? (
                    <tr><td style={tdStyle} colSpan={6}>No page views in this window.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </TableShell>
          </section>

          <section style={{ display: 'grid', gap: 8, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#172d33', fontWeight: 850 }}>
              <AlertTriangle size={16} /> Recent errors
            </div>
            <div style={{ ...panel, overflow: 'hidden', display: 'grid', gridTemplateRows: 'minmax(180px, 260px) minmax(220px, 1fr)' }}>
              <div style={{ overflow: 'auto', borderBottom: '1px solid #e4ece9' }}>
                {(data?.errors || []).map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setSelectedError(row)}
                    style={{
                      width: '100%',
                      border: 0,
                      borderBottom: '1px solid #eef3f1',
                      background: selectedError?.id === row.id ? '#edf6f4' : '#fff',
                      padding: 10,
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'grid',
                      gap: 4,
                    }}
                  >
                    <span style={{ fontSize: 12, color: '#172d33', fontWeight: 800 }}>{short(row.message, 110)}</span>
                    <span style={{ fontSize: 11, color: '#6d7c80' }}>
                      {fmtDate(row.created_at)} · {row.email || 'unknown user'} · {row.event}
                      {row.status ? ` · ${row.status}` : ''}
                      {contextText(row, 'request_path') ? ` · ${contextText(row, 'request_path')}` : ''}
                    </span>
                  </button>
                ))}
                {!loading && !(data?.errors || []).length ? (
                  <div style={{ padding: 12, fontSize: 12, color: '#6d7c80' }}>No captured errors in this window.</div>
                ) : null}
              </div>
              <pre style={{ margin: 0, padding: 12, overflow: 'auto', fontSize: 11, lineHeight: 1.45, color: '#172d33', background: '#fbfcfb' }}>
                {selectedError ? JSON.stringify(selectedError, null, 2) : 'Select an error to inspect context.'}
              </pre>
            </div>
          </section>
        </div>

        <section style={{ display: 'grid', gap: 8 }}>
          <div style={{ color: '#172d33', fontWeight: 850 }}>Recent sessions</div>
          <TableShell>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1040 }}>
              <thead>
                <tr>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Started</th>
                  <th style={thStyle}>Last seen</th>
                  <th style={thStyle}>Views</th>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>Errors</th>
                  <th style={thStyle}>Last path</th>
                </tr>
              </thead>
              <tbody>
                {(data?.sessions || []).map((row) => (
                  <tr key={row.session_id}>
                    <td style={tdStyle}><b>{row.email}</b><div style={{ color: '#7a878a' }}>{row.session_id}</div></td>
                    <td style={tdStyle}>{fmtDate(row.created_at)}</td>
                    <td style={tdStyle}>{fmtDate(row.last_seen_at)}</td>
                    <td style={tdStyle}>{fmtInt(row.page_view_count)}</td>
                    <td style={tdStyle}>{fmtDuration(row.dwell_ms)}</td>
                    <td style={tdStyle}>{fmtInt(row.error_count)}</td>
                    <td style={tdStyle}>{short(row.last_path, 90)}</td>
                  </tr>
                ))}
                {!loading && !(data?.sessions || []).length ? (
                  <tr><td style={tdStyle} colSpan={7}>No sessions in this window.</td></tr>
                ) : null}
              </tbody>
            </table>
          </TableShell>
        </section>

        {loading ? <div style={{ fontSize: 12, color: '#5d6d72' }}>Loading activity...</div> : null}
      </div>
    </FuseGridSurfaceShell>
  )
}
